import React from "react";
import {
  List,
  SimpleList,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  Pagination,
  TextInput,
  BooleanInput,
  ReferenceArrayInput,
  SelectArrayInput,
  useRecordContext,
  BulkActionsToolbar,
} from "react-admin";

import { useMediaQuery } from "@mui/material";
import { exporter } from "./Exporter";
import BulkUpdateCategoryAndVariant from "./BulkUpdateCategoryAndVariant";
import BulkUploadProductImage from "./BulkUploadProductImage";

import { useAvailableCurrencies } from "../useAvailableCurrencies";
import ListActions from "./ListActions";
import CustomBulkDeleteButton from "./CustomBulkDeleteButton";
import CopyTemplateButton from "./CopyTemplateButton";
import { StateProvider, store } from "./ListSelectable";
import CustomChipArrayField from "../CustomChipArrayField";
import CustomItemDescription from "./CustomItemDescription";
import BulkPinCommentTemplate from "./BulkPinCommentTemplate";
import TextLinkField from "../Common/TextLinkField";
import CustomPickerInput from "../OrderItem/CustomPickerInput";
import CustomDateField from "../Common/CustomDateField";
import CustomIntegerInput from "../Common/CustomIntegerInput";
import BulkRemovePrefix from "./BulkRemovePrefix";
import BulkUpdateTierPrice from "./BulkUpdateTierPrice";
import TieredPricesField from "./TieredPricesField";
import { useProfileLocale } from "../Common/locale";
import BulkUpdateKeywordsButton from "./BulkUpdateKeywordsButton";
import { withSellerAdminShow } from "../DataHooks/user";
import { red, amber, green } from "@mui/material/colors";

const DEBOUNCE = 2000;

const BulkActionButtons = (props) => (
  <BulkActionsToolbar selectAllButton={false}>
    <BulkPinCommentTemplate {...props} />
    <BulkUploadProductImage {...props} />
    <BulkUpdateTierPrice {...props} />
    <BulkUpdateCategoryAndVariant {...props} />
    <BulkRemovePrefix {...props} />
    <BulkUpdateKeywordsButton {...props} />
    <CustomBulkDeleteButton {...props} />
  </BulkActionsToolbar>
);

const AMBER_COLOR = amber["A700"];
const RED_COLOR = red["A700"];
const GREEN_COLOR = green["A700"];

const tinyFont = { fontSize: "10px", opacity: 0.7 };
const redStyle = { color: RED_COLOR };
const normalStyle = { color: "inherit" };

const withStockStyle = (Wrapped) => (props) => {
  const { source } = props;
  const record = useRecordContext();
  const stockWarning =
    record && record["stock"] / 4 > record[source] && record[source] > 0;
  const stockEmpty = record && record[source] <= 0;
  const color = stockWarning ? AMBER_COLOR : stockEmpty ? RED_COLOR : undefined;
  return <Wrapped {...props} sx={{ color }} />;
};

const withActiveStyle = (Wrapped) => (props) => {
  const { source } = props;
  const record = useRecordContext();
  const isInactive = record && !record[source];
  const isActive = record && record[source] === true;

  const color = isInactive ? RED_COLOR : isActive ? GREEN_COLOR : undefined;
  return <Wrapped {...props} sx={{ color }} />;
};

const StockLeftNumberField = withStockStyle(NumberField);
const ActiveBooleanField = withActiveStyle(BooleanField);
const AdminOnlyNumberField = withSellerAdminShow(NumberField);

const defaultFilters = [
  <TextInput
    label="Keyword"
    source="key_containss"
    alwaysOn
    parse={(v) => v.toUpperCase()}
  />,
  <TextInput label="Item" source="description_contains" alwaysOn />,
  <BooleanInput label="Active" source="isActive" defaultValue={true} />,
  <BooleanInput
    label="Allow Backorder"
    source="allowBackorder"
    defaultValue={false}
  />,
  <BooleanInput
    label="Allow In Web"
    source="allowInWeb"
    defaultValue={false}
  />,
  <TextInput label="SKU" source="sku_contains" />,
  <ReferenceArrayInput
    label="Categories"
    source="categories"
    reference="categories"
    perPage={100}
  >
    <SelectArrayInput optionText="label" />
  </ReferenceArrayInput>,
  <ReferenceArrayInput
    label="Variants"
    source="variants"
    reference="variants"
    perPage={100}
  >
    <SelectArrayInput optionText="label" />
  </ReferenceArrayInput>,
  <CustomIntegerInput source="stockLeft_gte" label="Stock Left min" />,
  <CustomIntegerInput source="stockLeft_lte" label="Stock Left max" />,
  <CustomIntegerInput source="vipTier_gte" label="VIP min" />,
  <CustomIntegerInput source="vipTier_lte" label="VIP max" />,
  <CustomIntegerInput source="stock_gte" label="Stock min" />,
  <CustomIntegerInput source="stock_lte" label="Stock max" />,
  <CustomPickerInput source="createdAt_gte" label="CreatedAt start" />,
  <CustomPickerInput source="createdAt_lte" label="CreatedAt end" />,
  <CustomPickerInput source="updatedAt_gte" label="UpdatedAt start" />,
  <CustomPickerInput source="updatedAt_lte" label="UpdatedAt end" />,
];

const CustomDatagrid = (props) => {
  const { state } = React.useContext(store);
  const newChildren = React.Children.toArray(props.children).filter((child) =>
    child.props && child.props.source ? state[child.props.source] : true
  );
  return <Datagrid {...props}>{newChildren}</Datagrid>;
};

const KeywordList = (props) => {
  const locale = useProfileLocale();
  const { isMultiCurrency } = useAvailableCurrencies();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  return (
    <StateProvider>
      <List
        debounce={DEBOUNCE}
        {...props}
        actions={<ListActions />}
        exporter={exporter(locale)}
        filters={defaultFilters}
        pagination={
          <Pagination rowsPerPageOptions={[10, 25, 50, 100, 200, 500]} />
        }
        sort={{ field: "createdAt", order: "DESC" }}
      >
        {isSmall ? (
          <SimpleList
            primaryText={(record) => (
              <span style={record.isActive ? normalStyle : redStyle}>
                {record.description}
                {!record.isActive && <span style={tinyFont}> (inactive)</span>}
              </span>
            )}
            secondaryText={(record) => (
              <span
                style={
                  record.stock / 4 > record.stockLeft ? redStyle : normalStyle
                }
              >
                {`${record.stockLeft} of ${record.stock} left`}
              </span>
            )}
            tertiaryText={(record) => (
              <>
                <span style={tinyFont}>createdAt</span>
                <br />
                {new Date(record.createdAt).toLocaleDateString(locale)}
              </>
            )}
          />
        ) : (
          <CustomDatagrid
            rowClick="show"
            bulkActionsToolbar={<BulkActionButtons />}
          >
            <CustomItemDescription source="description" />
            {/* <TextField source="key" label="Keyword" /> */}
            <TextLinkField
              source="key"
              resourceName="keywords"
              resourceKeyId="id"
            />
            <TextField source="sku" />
            <ActiveBooleanField source="isActive" />
            {isMultiCurrency && <TextField source="currencyCode" />}
            <NumberField source="price" />
            <TieredPricesField ellipsis source="tieredPrices" />
            <TieredPricesField ellipsis source="webTieredPrices" />
            <NumberField source="webPrice" />
            <NumberField source="stock" />
            <StockLeftNumberField source="stockLeft" />
            <NumberField source="vipTier" />
            <ActiveBooleanField source="allowBackorder" />
            <NumberField source="weight" />
            <AdminOnlyNumberField source="cost" />
            <TextField source="detail" />
            <ActiveBooleanField source="allowInWeb" />
            <CustomChipArrayField source="categories" />
            <CustomChipArrayField source="variants" />
            <NumberField source="minQuantity" />
            <NumberField source="maxQuantity" />
            <CustomDateField source="createdAt" showTime />
            <CustomDateField source="updatedAt" showTime />
            <CopyTemplateButton />
          </CustomDatagrid>
        )}
      </List>
    </StateProvider>
  );
};

export default KeywordList;
